import { ArrowBack, SaveOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Divider,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { CircleCheckBig, ImagePlus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button } from "../../../components";
import { Loading } from "../../../components/loading";
import { useToastStore, useUserStore } from "../../../stores";
import { colorBgNegative, colorNegative, colorPrimary, textFieldStyle } from "../../../theme";
import { maskCurrencyBRL, maskPlaca } from "../../../utils/mask/MaskUtils";
import { AcessoriosDTO, AnuncioDetailsModal, CompanyDTO, CriarVeiculoAnuncioDTO, MarcasFormDTO, ModelosFormDTO, NovoVeiculoFormDTO, VehicleImage } from "../dto/CompanyDTO";
import { formatCurrency, getCombustivel, getCombustivelId, getImageSrc, getSelectedAcessorios, getTransmissaoId, getTransmissaoLabel, parseCurrencyToDecimal, resolveAssetUrl } from "../helpers/ComapnyHelpers";
import { acessoriosMock, combustiveis, transmissao, typeOfVehicle } from "../mocks/CompanyMocks";
import { deletePhoto, fetchAdDetailsById, fetchCompanies, fetchFipe, fetchMarcas, fetchModelo, fetchVeiculoEdit, updatePhotoCapa, updateVeiculo } from "../repository/ComapnyRepository";
import { AutoCompleteField } from "./AutoComplete";
import { CancelButton, SubmitButton } from "./ButtonComponent";
import { InfoRow } from "./InfoRowComponent";
import { LaudoCautelarLink } from "./LaudoCautelarComponent";
import { TextFieldController } from "./TextFieldComponent";

interface AdDetailsModalProps {
  adId: number;
  open: boolean;
  onClose: () => void;
  onSuccessEdit: () => Promise<void>;
  allowsEdit: boolean;
}

export function AdDetailsModal({
  adId,
  open,
  onClose,
  onSuccessEdit,
  allowsEdit,
}: AdDetailsModalProps) {
  const { user } = useUserStore();
  const { setToast } = useToastStore();
  const [isLoading, setLoading] = useState(true);
  const [anuncio, setAnuncio] = useState<AnuncioDetailsModal | null>(null);
  const [edit, setEdit] = useState<boolean>(false);
  const photosInputRef = useRef<HTMLInputElement | null>(null);
  const [acessorios, setAcessorios] = useState<AcessoriosDTO[]>(acessoriosMock);
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [laudoFile, setLaudoFile] = useState<File | null>(null);
  const [laudo, setLaudoLink] = useState<string | null>(null);
  const [companies, setCompanies]= useState<CompanyDTO [] | null>(null);
  const [marcas, setMarcas]= useState<MarcasFormDTO[]>([]);
  const [modelos, setModelos]= useState<ModelosFormDTO[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
  control,
  handleSubmit,
  formState: { errors },
  reset,
  setValue,
} = useForm<NovoVeiculoFormDTO>({ defaultValues: {
      companyId: null,
      placa: null,
      tipo_veiculo: null,
      marca: null,
      modelo: null,
      anoFabricacao: null,
      anoModelo: null,
      km: null,
      motor: null,
      combustivel: null,
      transmissao: null,
      cor: null,
      portas: null,
      valor_inicial: null,
      observacao: null,
      avarias: null,
      laudo: null,
    
    },});
  const selectedTypeOfVehicle = useWatch({
  control,
  name: "tipo_veiculo",
  });
const selectMarca = useWatch({
  control,
  name: "marca",
});

const handleRemovePhoto = async (indexToRemove: number) => {
  const image = images[indexToRemove];

  //  IMAGEM NOVA (File) → só remove local
  if (image.isNew) {
    if (image.previewUrl) {
      URL.revokeObjectURL(image.previewUrl);
    }

    setImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );

    return;
  }

  //  IMAGEM EXISTENTE (API)
  try {
    setLoading(true);

    await deletePhoto(
      image.adId!,
      image.url!
    );

    setImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  } catch (error) {
   setToast({variant:"error", message:"Erro ao remover imagem"});
  } finally {
    setLoading(false);
  }
};

const handleSetCover =async (index: number) => {
  const image = images[index];
  if (image.isNew) {
      setImages((prev) =>
    prev.map((img, i) => ({
      ...img,
      isCapa: i === index,
    }))
  );
  return;
  }
  try {
    const result = await updatePhotoCapa(image.adId!,0, image.url!);
    if (result ===true) {
  setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isCapa: i === index,
      }))
    );
    }
  } catch (error) {
   setToast({variant:"error", message:"Erro ao atualizar a capa"});
  } finally {
    setLoading(false);
  }

};

const handleSelectLaudo = (
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const file = event.target.files?.[0] ?? null;
  setLaudoFile(file);
};

const fetchCompanie = async () => {
  try {
    const userId = user?.id;
    const result = await fetchCompanies(userId);
    if (!result?.success) {
      return;
    }
    setCompanies(result.data);
  } catch (error) {
  }
}

 const handleToggleAcessorio = (id: number) => {
  setAcessorios((prev) =>
    prev.map((item) =>
      item.id === id
        ? { ...item, isActive: !item.isActive }
        : item
    )
  );
};

const handleAllowEdit = () => {
if (!allowsEdit) {
   setToast({variant:"error", message:"Veículo adicionado à lista. Remova e prossiga com a edição."});
  return;
}
 setEdit(true);
};

const onSubmitUpdate = async (data: NovoVeiculoFormDTO) => {
  setLoading(true);

  try {
    const fotos = images
      .filter(img => img.isNew && img.file)
      .map(img => ({
        foto: img.file as File,
        capa: img.isCapa,
      }));

    if (images.length === 0) {
      setToast({
        message: "Informe ao menos uma foto do veículo",
        variant: "error",
      });
      return;
    }
   const valorFipe = await fetchFipe(modelos.find(m => m.id === data.modelo)?.codigo_fipe.trim() ?? "", Number(data.anoModelo) );
    const payload: CriarVeiculoAnuncioDTO = {
      id_concessionaria: Number(data.companyId),

      placa: data.placa ?? "",
      tipo_veiculo: String(data.tipo_veiculo),
      marca: marcas.find(m => m.id === data.marca)?.label ?? "",
      modelo: modelos.find(m => m.id === data.modelo)?.label ?? "",

      ano_fabricacao: Number(data.anoFabricacao) ?? 0,
      ano_modelo: Number(data.anoModelo) ?? 0,
      km: Number(data.km) ?? 0,
      codigo_fipe: modelos.find((m) => m.id === data.modelo)?.codigo_fipe??"",
      valor_fipe:valorFipe,
      motor: data.motor ?? "",
      combustivel: getCombustivel(data.combustivel) ?? "",
      transmissao: getTransmissaoLabel(data.transmissao) ?? "",
      cor: data.cor ?? "",
      portas: Number(data.portas),

      valor: parseCurrencyToDecimal(data.valor_inicial),

      observacao: data.observacao ?? "",
      avarias: data.avarias ?? null,

      laudo_file: laudoFile,

      acessorios: getSelectedAcessorios(acessorios),
      fotos, // ✅ AGORA SIM
    };

    const result = await updateVeiculo(payload, adId);

    if (!result?.success) {
      setToast({
        message: result.message ?? "Erro. Contate o administrador.",
        variant: "error",
      });
      return;
    }

    setToast({
      message: result.message ?? "Veículo cadastrado com sucesso",
      variant: "success",
    });

    onSuccessEdit();
  } catch {
    setToast({
      message: "Erro. Contate o administrador.",
      variant: "error",
    });
  } finally {
    setLoading(false);
  }
};

const handleOpenPhotosDialog = () => {
  photosInputRef.current?.click();
};

const fetchAdDetailsByIdModal = async () => {
    try {
        const result = await fetchAdDetailsById(adId);
         if (!result?.success) {
            setToast({variant:"error", message:result?.message ?? "Erro. Não foi possível encontrar o veículo."});
            return;
        }
        setAnuncio(result.data);
        setLaudoLink(result.data.laudo);
    } catch (error) {
          setToast({variant:"error", message: "Erro. Não foi possível encontrar o veículo."});
    } finally {
        setLoading(false);
    }
};

const handleOpenFileDialog = (e?: React.MouseEvent) => {
  e?.preventDefault();
  e?.stopPropagation();
  fileInputRef.current?.click();
};

const getMarcas = async (typeVehicleId: number) => {
  try {
    setLoading(true);

    const result = await fetchMarcas(typeVehicleId);

    if (!result?.success) {
      setToast({variant:"error", message: result?.message ?? "Erro ao buscar as marcas"});
      return;
    }

    setMarcas(result.data);
  } catch {
    setToast({variant:"error", message:  "Erro ao buscar as marcas"});
  } finally {
    setLoading(false);
  }
};

const fetchModelos = async (marcaId: number) => {
  try {
    setLoading(true);
    const result = await fetchModelo(marcaId);
    if (!result?.success) {
       setToast({variant:"error", message: result?.message ?? "Erro ao buscar os Modelos."});
      return;
    }
    setModelos(result.data);
  } catch {
    setToast({variant:"error", message:"Erro ao buscar os Modelos."});
  } finally {
    setLoading(false);
  }
};


const handleSelectPhotosInput = (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const files = Array.from(e.target.files ?? []);

  setImages((prev) => {
    const hasCover = prev.some((i) => i.isCapa);

    const mapped: VehicleImage[] = files.map((file, index) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      isNew: true,
      isCapa: !hasCover && index === 0,
    }));

    return [...prev, ...mapped];
  });
};


const preloadAdData = async (anuncioId: number) => {
  try {
    setLoading(true);

   const data = await fetchVeiculoEdit(anuncioId);
    if (!data.data.id) return;
        const veiculo = data.data;
        const transmissaoId =   getTransmissaoId(veiculo.transmissao);
        const combustivelId = getCombustivelId(veiculo.combustivel);
    reset({
      companyId: veiculo.concessionaria_id,
      placa: veiculo.placa,
      tipo_veiculo: Number(veiculo.tipo_veiculo), 
      marca: veiculo.marca_id,
      modelo: veiculo.modelo_id,
      anoFabricacao: String(veiculo.num_ano_fabricacao),
      anoModelo: String(veiculo.num_ano_modelo),
      km: veiculo.km,
      motor: veiculo.motor,
      combustivel: combustivelId, // mapearemos abaixo
      transmissao: transmissaoId,
      laudo: veiculo.laudo,
      cor: veiculo.cor,
      portas: String(veiculo.portas),
      valor_inicial:formatCurrency(veiculo.val_inicial),
      observacao: veiculo.observacao,
      avarias: veiculo.avarias,
    });

    //  Acessórios
    setAcessorios((prev) =>
      prev.map((a) => ({
        ...a,
        isActive: veiculo.acessorios.some(
          (va: any) => va.id_acessorio === a.id
        ),
      }))
    );

    //  Fotos existentes (URLs)
    setImages(
      veiculo.fotos.map((file:any) => ({
        url: `${file.url_foto}`,
        isNew: false,
        adId: adId,
        isCapa: file.capa
      }))
    );

  } catch (error) {
   setToast({variant:"error", message:"Erro ao carregar anúncio."});
  } finally {
    setLoading(false);
  }
};
 

useEffect(() => {
    if (!open) return;
    fetchAdDetailsByIdModal();
  }, [adId, open]);



useEffect(() => {
  if (!edit) return;
  fetchCompanie();

  if (edit && adId) {
    preloadAdData(adId);
  }
}, [edit, adId]);


useEffect(() => {
  if (!selectedTypeOfVehicle) return;
  if(!edit) return;
  getMarcas(selectedTypeOfVehicle);
}, [selectedTypeOfVehicle]);

useEffect(() => {
  if (!selectMarca) return;
  if(!edit) return;
  fetchModelos(selectMarca);
}, [selectMarca]);

  return (
    <>
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxHeight: "85%",
        overflowY:"auto",
          bgcolor: isLoading? "transparent": "background.paper",
          borderRadius: 2,
          pr: 8,
          pl:8,
          pt:5,
          pb:5,
          outline: "none",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={240}
          >
            <Loading />
          </Box>
           ) :  
            edit ? (
            <> 
            <Box display="flex" flexDirection="column" width="100%" height="100%" gap={4} >
            <form onSubmit={handleSubmit(onSubmitUpdate)}>
              {/* FORMULÁRIO NOVO ANÚNCIO */}
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={4}
                flexGrow={1}
                width="100%"
              >
              <Box borderRadius={1} p={2} gap={1} bgcolor="white">
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Dados do veículo *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(1, 1fr)"
                    gap={2}
                    width="100%"
                  >
                  <AutoCompleteField
                    name="companyId"
                    control={control}
                    errors={errors}
                    options={companies ?? []}
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="placa"
                    label="Placa"
                    title="Placa"
                    placeholder="Ex: ABC-1234"
                    required
                    mask={maskPlaca}
                  />
                  <AutoCompleteField
                    control={control}
                    errors={errors}
                    name="tipo_veiculo"
                    label="Escolha o tipo de veículo"
                    required={true}
                    title="Tipo de veículo"
                    options={typeOfVehicle}
                  />
                  <AutoCompleteField
                    control={control}
                    errors={errors}
                    name="marca"
                    label="Escolha a marca do veículo"
                    required={true}
                    title="Marca"
                    options={marcas ?? []}
                  />
                  <AutoCompleteField
                    control={control}
                    errors={errors}
                    label="Escolha o modelo do veículo"
                    required={true}
                    name="modelo"
                    title="Modelo"
                    options={modelos ?? []}
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="anoFabricacao"
                    label="Ano de fabricação"
                    title="Ano de fabricação"
                    placeholder="Informe o ano de fabricação. Ex: 2025"
                    required
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="anoModelo"
                    label="Ano modelo"
                    title="Ano modelo"
                    placeholder="Informe o ano do modelo. Ex: 2026" 
                    required
                  />
                  </Box>
              </Box>
              <Box borderRadius={1} p={2} gap={1} bgcolor="white">
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Propriedades do veículo *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(1, 1fr)"
                    gap={2}
                    width="100%"
                  >
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="km"
                    label="Informe a quilometragem"
                    title="Quilometragem"
                    placeholder="Ex: 161.200"
                    required
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="motor"
                    title="Motor"
                    label="Motor"
                    placeholder="Informe o motor. Ex: 2.2"
                    required
                  />
                  <AutoCompleteField
                    control={control}
                    errors={errors}
                    name="combustivel"
                    label="Escolha o combustível"
                    required={true}
                    title="Combustível"
                    options={combustiveis ?? []}
                  />
                  <AutoCompleteField
                    control={control}
                    errors={errors}
                    label="Escolha a transmissão"
                    name="transmissao"
                    required={true}
                    title="Transmissão"
                    options={transmissao ?? []}
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="cor"
                    title="Cor"
                    label="Cor"
                    placeholder="Informe a cor do veículo. Ex: branca"
                    required
                  />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="portas"
                    label="Portas"
                    title="Número de portas"
                    placeholder="Informe a quantidade de portas." 
                    required
                  />
                  </Box>
                </Box>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={4}
                flexGrow={1}
                mt={2}
                width="100%"
              >
              <Box borderRadius={1} p={2} gap={1} bgcolor="white">

                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(1, 1fr)"
                    gap={2}
                    width="100%"
                  >
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Valores *
                  </Typography>
                  <Divider sx={{ width: "100%", my:0, borderBottomWidth: 2 }} />
                  <TextFieldController
                    control={control}
                    errors={errors}
                    name="valor_inicial"
                    label="Valor do veículo"
                    title="Valor do veículo"
                    placeholder="R$ 50.000,00"
                    required
                    mask={maskCurrencyBRL}
                  />
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Observações *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 0, borderBottomWidth: 2 }} />
                  <Controller
                    name="observacao"
                    control={control}
                    rules={{ required: "Texto padrão é obrigatório" }}
                    render={({ field }) => (
                    <TextField
                      {...field}
                      sx={textFieldStyle}
                      fullWidth
                      multiline
                      rows={4}
                      label="Observações essenciais"
                      placeholder="Escreva observações relevantes sobre o veículo para aparecer na 
            tela do anúncio de cotação."
                      error={!!errors?.placa}
                      helperText={errors?.placa?.message}
                    />
                    )}
                  />
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Avarias *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 0, borderBottomWidth: 2 }} />
                  <Controller
                    name="avarias"
                    control={control}
                    rules={{ required: "Texto padrão é obrigatório" }}
                    render={({ field }) => (
                    <TextField
                      {...field}
                      sx={textFieldStyle}
                      fullWidth
                      multiline
                      rows={4}
                      label="Avarias & Defeitos"
                      placeholder="Escreva possíveis avarias do veículo."
                      error={!!errors?.placa}
                      helperText={errors?.placa?.message}
                    />
                    )}
                  />
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                  >
                    Laudo *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 0, borderBottomWidth: 2 }} />
                  <Box
                    mt={2}
                    p={2}
                    borderRadius={2}
                    display="flex"
                    alignItems="center"
                    flexDirection={"column"}
                    gap={2}
                    bgcolor="white"
                    border="1px solid #dee2e6"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handleSelectLaudo}
                    />
                    <Stack display={"flex"} flexDirection={"column"} gap={2} alignContent={"flex-start"} justifyContent={"flex-start"} alignItems={"center"}>
                        <Button
                            variant="contained"
                            type="button"
                            onClick={(e) => handleOpenFileDialog(e)}
                        >
                      Selecionar arquivo
                    </Button>

                    <Typography
                      fontSize="0.9rem"
                      color={laudoFile ? "black" : "text.secondary"}
                    >
                      {laudoFile
                        ? laudoFile.name
                        : laudo ?"Observação: Ao realizar o upload de um novo laudo e clicar no botão salvar, o laudo anterior será substituído." : "Nenhum arquivo selecionado"}
                    </Typography>
                    </Stack>
                    {adId && !laudoFile && (
                      <a
                        href={resolveAssetUrl(laudo ?? "", adId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <Typography
                          fontWeight={400}
                          fontSize="1rem"
                          color={colorPrimary}
                          sx={{ cursor: "pointer" }}
                        >
                          Visualizar Laudo cautelar
                        </Typography>
                      </a>
                    )}
                  </Box>
            <Typography
              fontWeight="bold"
              fontSize="1.3rem"
              color="black"
              mt={2}
            >
              Fotos do veículo *
            </Typography>

            <Divider sx={{ width: "100%", my: 0, borderBottomWidth: 2 }} />

            {/* INPUT ESCONDIDO */}
            <input
              ref={photosInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleSelectPhotosInput}
            />

            <Box
              mt={2}
              p={4}
              borderRadius={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              bgcolor="white"
              border="2px dashed #dee2e6"
              sx={{
                cursor: "pointer",
                transition: "all .2s ease",
                "&:hover": {
                  borderColor: colorPrimary,
                  backgroundColor: "rgba(255, 102, 0, 0.04)",
                },
              }}
              onClick={handleOpenPhotosDialog}
            >
              <ImagePlus size={40} color={colorPrimary} />

              <Typography
                fontWeight={600}
                fontSize="0.95rem"
                color="black"
              >
                Adicionar fotos
              </Typography>

              <Typography
                fontSize="0.8rem"
                color="text.secondary"
              >
                {images.length > 0
                  ? `${images.length} foto(s) selecionada(s)`
                  : "Clique para selecionar uma ou mais fotos"}
              </Typography>
            </Box>
            {images.length > 0 && (
              <Box
                mt={2}
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
                gap={2}
              >
                {images.map((image, index) => {
                  const src = getImageSrc(image);
                  return (
                    <Box
                      key={`${image.url ?? image.file?.name}-${index}`}
                      position="relative"
                      borderRadius={2}
                      overflow="hidden"
                      border="1px solid #dee2e6"
                      sx={{
                        cursor: "pointer",
                        "&:hover .overlay": {
                          opacity: 1,
                        },
                      }}
                    >
                      {/* IMAGEM */}
                      <img
                        src={src}
                        alt="Foto do veículo"
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {/* BADGE CAPA */}
                      {image.isCapa && (
                        <Box
                          position="absolute"
                          top={8}
                          left={8}
                          px={1}
                          py={0.3}
                          borderRadius={1}
                          bgcolor={colorPrimary}
                          color="white"
                          fontSize="0.7rem"
                          fontWeight={600}
                          zIndex={2}
                        >
                          CAPA
                        </Box>
                      )}
            {/* OVERLAY */}
            <Box
              className="overlay"
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              bgcolor="rgba(0,0,0,0.55)"
              sx={{
                inset: 0,
                opacity: 0,
                transition: "opacity .2s ease",
              }}
            >

              {/* DEFINIR CAPA */}
              {!image.isCapa && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetCover(index);
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all .2s ease",
                    "&:hover": {
                      backgroundColor: colorPrimary,
                      color: "#fff",
                    },
                  }}
                  title="Definir como capa"
                >
                  ⭐
                </Box>
              )}

              {/* REMOVER */}
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemovePhoto(index);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all .2s ease",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                  },
                }}
                title="Remover foto"
              >
                <Trash2 size={20} />
              </Box>
            </Box>


                      {/* NOME / STATUS */}
                      <Typography
                        fontSize="0.7rem"
                        p={0.5}
                        noWrap
                        bgcolor="white"
                        title={image.file?.name ?? image.url}
                      >
                        {image.file?.name ?? "Imagem cadastrada"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

                </Box>
              </Box>
              <Box borderRadius={1} p={2} gap={1} bgcolor="white">
                  <Typography
                    fontWeight="bold"
                    fontSize="1.3rem"
                    color="black"
                    mt={2}
                    mb={1}
                  >
                    Acessórios do veículo *
                  </Typography>
                  <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                  <Box
                    mt={2}
                    display="grid"
                    gridTemplateColumns="repeat(2, 1fr)"
                    gap={1}
                    width="100%"
                  >
                    {acessorios.map((item) => (
                      <Box
                        key={item.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Checkbox
                          checked={item.isActive}
                          onChange={() => handleToggleAcessorio(item.id)}
                          sx={{
                            color: "#dee2e6",
                            "&.Mui-checked": {
                              color: colorPrimary,
                            },
                          }}
                        />

                        <Typography
                          fontSize="0.95rem"
                          color="black"
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
              </Box>
              </Box>
              <Stack display={"flex"} flexDirection={"row"} mt={2} flexGrow={1} justifyItems={"center"} alignContent={"center"} alignItems={"center"} justifyContent={"flex-end"} gap={2}>
                <div>
              <Button
                variant='text'
                onClick={() => {
                  setEdit(false);
                }}
                type='button'
              >
                <ArrowBack fontSize='medium' />
                <span>Voltar</span>
              </Button>
                </div>
              <Button
              fitContent={true}
              variant="large"
              type="submit"
              >
              <SaveOutlined fontSize='medium' />
              Salvar
              </Button>
              </Stack>
              </form>
            </Box>
            </>
        ) : (
            <>
              <Typography fontWeight={600} fontSize="1.2rem">
                {anuncio?.marca} - {anuncio?.modelo}
              </Typography>
              <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
              <Stack display={"flex"} flexDirection={"row"} flexGrow={1} gap={1} alignContent={"flex-start"} alignItems={"center"}>
                          <Typography fontWeight={400}>
                              Status do veículo vinculado:
                          </Typography>
                          <Box
                          sx={{
                          width: 80,
                          height: 22,
                          borderRadius: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color:  colorNegative,
                          bgcolor: colorBgNegative ,
                          }}
                          >
                          {'Inativo'}
                          </Box>
              </Stack>
              <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              gap={8}
              width="100%"
              mt={4}
              >
                  <Box display={"flex"} flexDirection={"column"}>
                      <Typography fontWeight={600} fontSize="1.4rem">
                      Dados do veículo
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(1, 1fr)"
                      gap={2}
                      width="100%"
                      mt={1}
                      >
                      <InfoRow
                      label="Concessionária"
                      value={anuncio?.concessionaria_id}
                      />
                      <InfoRow
                      label="Placa"
                      value={anuncio?.placa}
                      />
                      <InfoRow
                      label="Tipo do veículo"
                      value={anuncio?.tipo_veiculo}
                      />
                      <InfoRow
                      label="Marca"
                      value={anuncio?.marca}
                      />
                      <InfoRow
                      label="Modelo"
                      value={anuncio?.modelo}
                      />
                      <InfoRow
                      label="Ano de fabricação"
                      value={anuncio?.num_ano_fabricacao}
                      />
                      <InfoRow
                      label="Ano o veículo"
                      value={anuncio?.num_ano_modelo}
                      />
                      </Box>
                  </Box>
                  <Box display={"flex"} flexDirection={"column"}>
                      <Typography fontWeight={600} fontSize="1.4rem">
                      Propriedades do veículo
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(1, 1fr)"
                      gap={2}
                      width="100%"
                      mt={1}
                      >
                      <InfoRow
                      label="Quilometragem"
                      value={anuncio?.km}
                      />
                      <InfoRow
                      label="Motor"
                      value={anuncio?.motor}
                      />
                      <InfoRow
                      label="Combustível"
                      value={anuncio?.combustivel}
                      />
                      <InfoRow
                      label="Transmissão"
                      value={anuncio?.transmissao}
                      />
                      <InfoRow
                      label="Cor"
                      value={anuncio?.cor}
                      />
                      <InfoRow
                      label="Número de portas"
                      value={anuncio?.portas}
                      />
                      </Box>
                  </Box>
              </Box>
              <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              gap={8}
              width="100%"
              mt={4}
              >
                  <Box display={"flex"} flexDirection={"column"}>
                      <Typography fontWeight={600} fontSize="1.4rem">
                      Avarias
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(1, 1fr)"
                      gap={2}
                      width="100%"
                      mt={1}
                      >
                      <Typography fontWeight={300} color={"black"} fontSize={"1rem"}>
                          {anuncio?.avarias}
                      </Typography>
                      </Box>
                  </Box>
                  <Box display={"flex"} flexDirection={"column"}>
                      <Typography fontWeight={600} fontSize="1.4rem">
                      Observações
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(1, 1fr)"
                      gap={2}
                      width="100%"
                      mt={1}
                      >
                      <Typography fontWeight={300} color={"black"} fontSize={"1rem"}>
                          {anuncio?.observacao}
                      </Typography>
                      </Box>
                  </Box>
              </Box>
              <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              gap={8}
              width="100%"
              mt={4}
              >
                  <Box display={"flex"} flexDirection={'column'}>
                      <Typography fontWeight={600} fontSize="1.2rem">
                          Laudo
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <LaudoCautelarLink
                      anuncioId={anuncio?.id}
                      laudo={anuncio?.laudo}
                      />
                      <Typography fontWeight={600} fontSize="1.2rem" mt={6}>
                          Fotos do veículo
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(2, 1fr)"
                      gap={4}
                      width="100%"
                      mt={4}
                      >
                      {anuncio?.fotos && anuncio.fotos.length > 0 ? (
                        anuncio.fotos.map((foto, index) => (
                          <Box
                            key={`${foto.url_foto}/${index}`}
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: 180,
                              borderRadius: 2,
                              overflow: "hidden",
                              bgcolor: "#f5f5f5",
                              cursor: "pointer",
                              transition: "all 0.3s ease",

                              "&:hover": {
                                boxShadow: "0 10px 28px rgba(0,0,0,0.25)",
                                transform: "translateY(-4px)",
                              },

                              "&:hover img": {
                                transform: "scale(1.05)",
                              },

                              "&::after": {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                background:
                                  "linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0))",
                                opacity: 0,
                                transition: "opacity 0.3s ease",
                              },

                              "&:hover::after": {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={resolveAssetUrl(foto.url_foto ?? "", anuncio.id)}
                              alt="Foto do anúncio"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.3s ease",
                              }}
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography fontSize="0.95rem" color="text.secondary">
                          Nenhuma imagem disponível.
                        </Typography>
                      )}

                      </Box>
                  </Box>
                  <Box>
                      <Typography fontWeight={600} fontSize="1.2rem">
                          Acessórios do veículo
                      </Typography>
                      <Divider sx={{ width: "100%", my: 2, borderBottomWidth: 2 }} />
                      <Box
                      display="grid"
                      gridTemplateColumns="repeat(2, 1fr)"
                      gap={2}
                      width="100%"
                      mt={4}
                      >
                      {anuncio?.acessorios && anuncio.acessorios.length > 0 ? (
                        anuncio.acessorios.map((a, index) => (
                          <Stack
                            key={index}
                            direction="row"
                            alignItems="center"
                            gap={0.8}
                            px={1.5}
                            py={0.8}
                            borderRadius={2}
                            sx={{
                              cursor: "default",
                              transition: "all 0.25s ease",
                              backgroundColor: "transparent",

                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                                boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <CircleCheckBig color={colorPrimary} size={18} />

                            <Typography
                              fontWeight={600}
                              color={colorPrimary}
                              fontSize="0.95rem"
                            >
                              {a.acessorio}
                            </Typography>
                          </Stack>
                        ))
                      ) : (
                        <Typography fontSize="0.95rem" color="text.secondary">
                          Nenhum acessório disponível.
                        </Typography>
                      )}

                      </Box>
                  </Box>
              </Box>
              <Stack
              direction="row"
              justifyContent="flex-end"
              mt={4}
              gap={2}
              >
                  <CancelButton
                      onClick={() => {
                      onClose();
                      }}
                      disabled={false}
                  />
                  <SubmitButton
                  title="Editar dados"
                      onClick={() => handleAllowEdit()}
                      disabled={false}
                  />
              </Stack>
            </>
        )}
      </Box>
    </Modal>
    </>
  );
}
